/**
 * Club opening-book binary (CDB1) — compact polyglot-style packaging of the JS book.
 * Not a Lichess/polyglot dump; same lines/weights as openingBook.js.
 *
 * Layout:
 *   magic "CDB1" | uint32BE count |
 *   repeat: uint16BE pathLen | uint16BE weight | uint8 ecoLen | uint8 nameLen |
 *           path utf8 | eco utf8 | name utf8
 */

let cached = null;
let loadPromise = null;

function utf8Decode(bytes) {
  if (typeof TextDecoder !== 'undefined') {
    return new TextDecoder().decode(bytes);
  }
  // Jest / older Node without TextDecoder
  // eslint-disable-next-line no-undef
  return Buffer.from(bytes).toString('utf8');
}

function decodeEntries(buffer) {
  const view = new DataView(buffer);
  const magic = String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3));
  if (magic !== 'CDB1') {
    throw new Error('Unsupported opening book magic: ' + magic);
  }
  const count = view.getUint32(4, false);
  const map = new Map();
  let offset = 8;
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < count; i += 1) {
    const pathLen = view.getUint16(offset, false); offset += 2;
    const weight = view.getUint16(offset, false); offset += 2;
    const ecoLen = view.getUint8(offset); offset += 1;
    const nameLen = view.getUint8(offset); offset += 1;
    const path = utf8Decode(bytes.subarray(offset, offset + pathLen)); offset += pathLen;
    const eco = utf8Decode(bytes.subarray(offset, offset + ecoLen)); offset += ecoLen;
    const name = utf8Decode(bytes.subarray(offset, offset + nameLen)); offset += nameLen;
    map.set(path, { eco, name, weight });
  }
  return map;
}

export async function loadClubBookBin(url = '/opening/club-book.bin') {
  if (cached) return cached;
  if (!loadPromise) {
    loadPromise = fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to load club-book.bin');
        return r.arrayBuffer();
      })
      .then((buf) => {
        cached = decodeEntries(buf);
        return cached;
      })
      .catch((err) => {
        loadPromise = null;
        throw err;
      });
  }
  return loadPromise;
}

export function clubBookSize() {
  return cached ? cached.size : 0;
}

export function lookupFromBin(uciMoves, bookMap = cached) {
  if (!bookMap || !Array.isArray(uciMoves) || uciMoves.length === 0) return null;
  const max = Math.min(uciMoves.length, 16);
  let best = null;
  for (let n = 1; n <= max; n += 1) {
    const key = uciMoves.slice(0, n).map((u) => String(u).toLowerCase()).join(' ');
    if (bookMap.has(key)) best = bookMap.get(key);
  }
  return best;
}

export function bookReplyFromBin(uciMoves, bookMap = cached) {
  if (!bookMap || !Array.isArray(uciMoves)) return null;
  const candidates = bookCandidatesFromBin(uciMoves, bookMap);
  if (!candidates.length) return null;
  const total = candidates.reduce((s, c) => s + c.weight, 0);
  let r = Math.random() * total;
  for (const c of candidates) {
    r -= c.weight;
    if (r <= 0) return c.uci;
  }
  return candidates[candidates.length - 1].uci;
}

/** Weighted next-move candidates from the CDB1 map (sorted by weight desc). */
export function bookCandidatesFromBin(uciMoves, bookMap = cached) {
  if (!bookMap || !Array.isArray(uciMoves)) return [];
  const key = uciMoves.map((u) => String(u).toLowerCase()).join(' ');
  const prefix = key ? `${key} ` : '';
  const depth = uciMoves.length + 1;
  const candidates = [];
  for (const [k, meta] of bookMap.entries()) {
    if ((key ? k.startsWith(prefix) : !k.includes(' ')) && k.split(' ').length === depth) {
      candidates.push({
        uci: k.split(' ').pop(),
        weight: Math.max(1, meta.weight || 1),
        eco: meta.eco,
        name: meta.name,
      });
    }
  }
  return candidates.sort((a, b) => b.weight - a.weight);
}

/** Test helper: decode without fetch. */
export function decodeClubBookForTest(buffer) {
  return decodeEntries(buffer);
}
